<template>
    <div id="LeaveDetails">
        <loading :active.sync="loading" :can-cancel="false" :is-full-page="true"
            :height="150" :width="150" :color="'#FFFF00'" :background-color="'#000000'" :opacity="0.5">
        </loading>
        <div v-if="!loading && leaveDetail">

            <!-- Row 1: Leave Info + Extra Details -->
            <v-row class="mx-2 mt-2">
                <!-- Left: Leave Info Card -->
                <v-col cols="12" md="6">
                    <div class="la-card">
                        <div class="la-card-header">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                            {{ $t('selfserviceleaveapproval.leaveapproval') }}
                        </div>
                        <div class="la-card-body">
                            <div class="la-row">
                                <span class="la-label">{{ $t('columns.staff') }}</span>
                                <span class="la-value">{{ leaveDetail.staffcode }} - {{ leaveDetail.psnname }}</span>
                            </div>
                            <div class="la-row">
                                <span class="la-label">{{ $t('generateunpaidleaverecord.leavetype') }}</span>
                                <span class="la-value">{{ leaveDetail.leavedesc }}</span>
                            </div>
                            <div class="la-row">
                                <span class="la-label">{{ $t('columns.period') }}</span>
                                <span class="la-value">{{ leaveDetail.startdate }} — {{ leaveDetail.enddate }}
                                    <span class="la-badge">{{ leaveDetail.lvdays }} {{ $t('selfserviceleaveapproval.day') }}</span>
                                </span>
                            </div>
                        </div>
                        <!-- Remark + standard button bar -->
                        <div class="la-card-body" style="border-top:1px solid #F1F5F9;padding-top:12px;">
                            <v-text-field
                                v-model="leaveDetail.aprvremark"
                                :label="$t('selfserviceleaveapproval.approrrej')"
                                outlined dense background-color="white"
                            ></v-text-field>
                        </div>
                        <div class="la-action-bar">
                            <v-btn class="fbtn fbtn-success" @click="approve(leaveDetail)">
                                <v-icon size="15" class="mr-1">mdi-check</v-icon>
                                {{ $t('taskapprovemobile.approve') }}
                            </v-btn>
                            <v-btn class="fbtn fbtn-danger" @click="reject(leaveDetail)">
                                <v-icon size="15" class="mr-1">mdi-close</v-icon>
                                {{ $t('taskapprovemobile.reject') }}
                            </v-btn>
                            <v-btn class="fbtn fbtn-secondary" @click="finalApprove(leaveDetail)">
                                <v-icon size="15" class="mr-1">mdi-check-all</v-icon>
                                {{ $t('taskapprovemobile.finalapprove') }}
                            </v-btn>
                        </div>
                    </div>
                </v-col>

                <!-- Right: Additional Info Card -->
                <v-col cols="12" md="6">
                    <div class="la-card">
                        <div class="la-card-header">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                            {{ $t('payeeinputprocess.details') }}
                        </div>
                        <div class="la-card-body">
                            <div class="la-row">
                                <span class="la-label">{{ $t('columns.reason') }}</span>
                                <span class="la-value">{{ leaveDetail.reasondesc || '-' }}</span>
                            </div>
                            <div class="la-row">
                                <span class="la-label">{{ $t('columns.relperson') }}</span>
                                <span class="la-value">{{ leaveDetail.reliefname || '-' }}</span>
                            </div>
                            <div class="la-row">
                                <span class="la-label">{{ $t('columns.remark') }}</span>
                                <span class="la-value">{{ leaveDetail.lvremarks || '-' }}</span>
                            </div>
                            <div class="la-row">
                                <span class="la-label">{{ $t('recdisciplinaryrec.supportingdoc') }}</span>
                                <span class="la-value">
                                    <span v-if="leaveDetail.filetype" class="la-link" @click="loadAttachment(leaveDetail.filetype, leaveDetail.attachment)">
                                        {{ leaveDetail.filetype }}
                                        <v-icon small>mdi-eye</v-icon>
                                    </span>
                                    <span v-else>-</span>
                                </span>
                            </div>
                        </div>
                    </div>
                </v-col>
            </v-row>

            <!-- Row 2: Approval Info + Leave Calendar -->
            <v-row class="mx-2 mt-3">
                <!-- Approval Info -->
                <v-col cols="12" md="6">
                    <div class="la-card">
                        <div class="la-card-header la-card-header-green">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                            {{ $t('selfserviceleaveapproval.subapprinfo') }}
                        </div>
                        <div class="la-card-body">
                            <div class="la-row">
                                <span class="la-label">{{ $t('homemobile.submitted') }}</span>
                                <span class="la-value">{{ $t('selfserviceclaimapproval.by') }} {{ leaveDetail.staffcode }} {{ leaveDetail.psnname }} {{ $t('homemobile.timerelatedon') }} {{ leaveDetail.entrytime }}</span>
                            </div>
                            <div class="la-label mt-2 mb-1">{{ $t('selfserviceleaveapproval.approval') }}</div>
                            <div v-for="(item, index) in staffApproveList" :key="index" class="la-approval-row">
                                <span class="la-value">{{ $t('selfserviceclaimapproval.by') }} {{ item.staffcode }} {{ item.psnname }}</span>
                                <span class="la-status-badge" :class="item.aprstatus === 'APPROVED' ? 'badge-approved' : 'badge-rejected'">{{ item.aprstatus }}</span>
                                <span class="la-value ml-2">{{ $t('homemobile.timerelatedon') }} {{ item.aprdate }}</span>
                            </div>
                        </div>
                    </div>
                </v-col>

                <!-- Leave Calendar -->
                <v-col cols="12" md="6">
                    <div style="border-radius:12px; border:1px solid #E8EDF5; box-shadow:0 1px 6px rgba(0,0,0,0.05); display:flex; flex-direction:column; height:700px; overflow:hidden;">
                        <div class="la-card-header la-card-header-blue" style="flex-shrink:0; border-radius:12px 12px 0 0;">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-right:6px;"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                            {{ $t('selfserviceleaveapproval.leavecalendar') }}
                        </div>
                        <div style="overflow-y:auto; flex:1; padding:12px 16px 16px 16px;">
                            <!-- Checkboxes aligned center -->
                            <div style="display:flex; align-items:center; gap:24px; margin-bottom:16px; margin-top:4px;">
                                <div style="display:flex; align-items:center; gap:6px;">
                                    <v-checkbox @change="checkFilter()" v-model="samecompany" dense hide-details style="margin:0; padding:0;"></v-checkbox>
                                    <span class="la-value">{{ $t('leavemobile.samecompany') }}</span>
                                </div>
                                <div style="display:flex; align-items:center; gap:6px;">
                                    <v-checkbox @change="checkFilter()" v-model="samedept" dense hide-details style="margin:0; padding:0;"></v-checkbox>
                                    <span class="la-value">{{ $t('leavemobile.samedepartment') }}</span>
                                </div>
                            </div>
                            <div class="la-calendar-card">
                                <calendar-view :show-date="showDate" :events="calendaritems" :key="calendarKey" class="theme-modern-leave" event-top="30px" @click-date="onClickDate">
                                    <template #header="{ headerProps }">
                                        <calendar-view-header :header-props="headerProps" @input="setShowDate" />
                                    </template>
                                </calendar-view>
                            </div>
                            <div class="mt-3 pb-2">
                                <div class="la-cal-date"><span class="la-cal-label">{{ $t('dayname.today') }} {{ todaydate }}</span> <span class="la-pct">{{ totalStaffPresentToday }}%</span></div>
                                <div v-for="item in todayStaffListLeave" :key="item.listid" class="la-cal-item">
                                    <span :class="item.lvlstatusholder=='(A)' ? 'cal-a' : 'cal-p'">{{ item.lvlstatusholder }}</span>
                                    {{ item.leavecode }} - <span class="la-grey">{{ item.psnname }}</span>
                                </div>
                                <div class="la-cal-date mt-2"><span class="la-cal-label">{{ $t('dayname.tomorrow') }} {{ tomorrowdate }}</span> <span class="la-pct">{{ totalStaffPresentTomorrow }}%</span></div>
                                <div v-for="item in tomorrowStaffListLeave" :key="item.listid" class="la-cal-item">
                                    <span :class="item.lvlstatusholder=='(A)' ? 'cal-a' : 'cal-p'">{{ item.lvlstatusholder }}</span>
                                    {{ item.leavecode }} - <span class="la-grey">{{ item.psnname }}</span>
                                </div>
                                <div v-if="loaddate" class="la-cal-date mt-2"><span class="la-cal-label">{{ loaddate }}</span></div>
                                <div v-for="item in loadStaffListLeave" :key="item.listid" class="la-cal-item">
                                    <span :class="item.lvlstatusholder=='(A)' ? 'cal-a' : 'cal-p'">{{ item.lvlstatusholder }}</span>
                                    {{ item.leavecode }} - <span class="la-grey">{{ item.psnname }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </v-col>
            </v-row>

            <!-- Dialogs -->
            <app-alert-event v-bind:subDialog="subDialog" v-bind:subDialogId="subDialogId"
                v-bind:subDialogTitle="subDialogTitle" v-bind:subDialogText="subDialogText"
                v-bind:subDetailTask="subDetailTask" v-on:saveAction="saveAction" v-on:cancelAction="cancelAction" />
            <app-staff-list v-bind:subDialogStaffList="subDialogStaffList" v-bind:subDialogStaffListParam="subDialogStaffListParam"
                v-on:backAction="backAction" v-on:choosenPerson="choosenPerson" />
            <app-alert-error v-bind:errorDialog="errorDialog" v-bind:errorMessage="errorMessage" v-on:OkPressed="dismissHandler" />
            <app-alert-error2 v-bind:errorDialog="errorDialog2" v-bind:errorMessage="errorMessage2" v-on:OkPressed="goBackHandler" />
            <app-confirm v-if="continueOrSelectDialog" v-bind:parentObject="{ isConfirm: continueOrSelectDialog, messageConfirm: continueOrSelectMessage, showContinueOption: pendingContinueAllowed, showSelectOption: true, showCancelOption: false, okLabel: 'Continue', selectLabel: 'Select' }"
                v-on:confirm="continueOrSelectYes" v-on:selectConfirm="continueOrSelectSelect" />
        </div>
    </div>
</template>

<script>
import AlertEvent from '../common/AlertEvent.vue';
import Loading from "vue-loading-overlay";
import StaffListEvent from '../common/StaffList.vue'
import ErrorMessageSelService from '../../sscomponents/common/ErrorMessage.vue'
import ConfirmDialog from '../../components/common/ConfirmDialog.vue'
import moment from 'moment'
import { CalendarView, CalendarViewHeader } from "vue-simple-calendar";
import "vue-simple-calendar/static/css/default.css";
import "vue-simple-calendar/static/css/holidays-us.css";

export default {

    components: {
        "app-alert-event": AlertEvent,
        "app-staff-list": StaffListEvent,
        "app-alert-error": ErrorMessageSelService,
        "app-alert-error2": ErrorMessageSelService,
        "app-confirm": ConfirmDialog,
        Loading,
        CalendarView,
        CalendarViewHeader
    },

    props: {
        leaveDetail: Object,
        staffApproveList: Array
    },

    data() {
        return {
            subDialog: false,
            subDialogTitle: '',
            subDialogText: '',
            subDialogId: null,
            subDetailTask: null,
            loading: true,

            samedept: null,
            samecompany: null,

            subDialogStaffList: false,
            subDialogStaffListParam: null,

            errorDialog: false,
            errorMessage: '',

            errorDialog2: false,
            errorMessage2: '',

            continueOrSelectDialog: false,
            continueOrSelectMessage: '',
            pendingApprovalValue: null,
            pendingSkipLevelNo: null,
            pendingContinueAllowed: true,

            showDate: new Date(),
            loaddate: null,
            leaverecordList: [],
            loadDateRecord: [],
            loadStaffListLeave: [],
            calendaritems: [],
            startMonthDate: moment(new Date()).startOf('month').format("YYYY-MM-DD"),
            endMonthDate: moment(new Date()).endOf('month').format("YYYY-MM-DD"),
            totalStaffMov: [],
            todaydate: null,
            todayStaffListLeave: [],
            todayStaffListHolder: [],
            totalStaffPresentToday: [],
            totalStaff: [],
            tomorrowdate: null,
            tomorrowStaffListLeave: [],
            totalStaffPresentTomorrow: [],

            staff: [],
            userHolder: [],
            backupDataItems: [],

            calendarKey: 0,

        }
    },

    watch: {

        // startMonthDate(){

        // const vm = this;
        // //let itemholder = []
        // let id = 0;

        // this.todayStaffListLeave = [];
        // this.tomorrowStaffListLeave = [];
        // this.loadStaffListLeave = [];

        // this.calendaritems = [];
        // // this.leaverecordList = [];
        // // this.todayStaffListHolder = [];
        // // this.totalStaff = [];
        // // this.totalStaffPresentToday = [];
        // // this.totalStaffPresentTomorrow = [];
        // // this.totalStaffMov = [];
        // this.loadDateRecord = [];

        // //this.$store.dispatch('get', {url: `/leaverecordwho/all/${this.$store.getters.userId}/${vm.startMonthDate}/${vm.endMonthDate}`})
        // //this.$store.dispatch('get', { url: `/leaverecordwhoselfservice/all/${vm.startMonthDate}/${vm.endMonthDate}`})
        // this.$store.dispatch('get', { url: `/leaverecordwho/all`})
        //     .then(async (res) => {

        //     let dataitems = res.data.rec
        //     vm.backupDataItems = dataitems;

        //     for (let i = 0; i < dataitems.length; i++) {
        //         let leaved = await this.checkDate(dataitems[i])
        //         dataitems[i].tleave = leaved.hday
        //         id++
        //     }

        //     if (id == dataitems.length) {

        //         // dataitems.map(val => {
        //         // let it2 = this.totalStaffMov.filter(val2 => {
        //         //     return val2.staffid == val.staffid && val2.recdate <= val.startdate && val2.recenddate >= val.enddate 
        //         // })[0]
        //         // val.emptypeid = it2.emptypeid
        //         // val.brchid = it2.brchid
        //         // val.compnyid = it2.compnyid
        //         // val.deptid = it2.deptid
        //         // val.gradeid = it2.grdid
        //         // return val
        //         // })

        //         let dateh = []
        //         for (let i = 0; i < dataitems.length; i++) {
        //         let getd = [...new Set(dataitems[i].tleave.map(item => item))]
        //         dateh = dateh.concat(getd)
        //         }
        //         let fdate = [...new Set(dateh.map(x => x))]
        //         let g = 0
        //         for (let i = 0; i < fdate.length; i++) {
        //         let dt = dataitems.filter(x => {
        //         let hol = x.tleave.indexOf(fdate[i])
        //         if (hol == -1) {
        //             return false
        //         }
        //         return x
        //         })

        //         let newdt = [...dt]

        //         for (let j = 0; j < newdt.length; j++) {
        //         let itm = {...newdt[j], cdate: fdate[i], listid: g++}
        //         vm.leaverecordList.push(itm)
        //         }

        //         vm.calendaritems.push({
        //         id: i+1,
        //         startDate: fdate[i],
        //         title: newdt.length
        //         })

        //     }

        //     console.log('watch data', vm.leaverecordList)
        //     this.loadRecordToday(vm.leaverecordList);
        //     this.loadRecordTomorrow(vm.leaverecordList);
        //     }
        //     })
        //     .catch((err) => {
        //     //req failure --> emit error
        //     vm.isErrTrigger = true;
        //     vm.errMsg = err;
        //     });
        // }

        async startMonthDate() {
            await this.resetLeaveState();
            await this.loadRecordCountList();
            // console.log("after load record, now doing the filter");
            await this.checkFilter();
        }
    },

    methods: {
        // Wilson: 04-03-2026, this is use by watch method, to reset leave information on date change
        async resetLeaveState() {
            this.todayStaffListLeave = [];
            this.tomorrowStaffListLeave = [];
            this.leaverecordList = [];
            this.calendaritems = [];
            this.loadDateRecord = [];
        },

        // Error Message Popup Dismiss
        dismissHandler() {
            this.errorDialog = false
            this.errorMessage = ''
        },

        //Button Checking
        finalApprove(param) {
            this.subDialogId = 1 //Final Approve ID
            this.subDialog = true
            this.subDialogTitle = this.$t('selfservicemytask.confirmfinapprove')
            this.subDialogText = this.$t('selfservicemytask.approvefintask')
            this.subDetailTask = param
        },

        reject(param) {
            if (param.aprvremark) {
                this.subDialogId = 2 //Reject ID
                this.subDialog = true
                this.subDialogTitle = this.$t('selfservicemytask.confirmrej')
                this.subDialogText = this.$t('selfservicemytask.rejecttask')
                this.subDetailTask = param
            } else {
                this.errorDialog = true
                this.errorMessage = this.$t('selfservicemytask.remcolrequired')
                // console.log(this.$t('selfservicemytask.remcolrequired'))
            }
        },

        approve(param) {
            this.subDialogId = 3 //Approve ID
            this.subDialog = true
            this.subDialogTitle = this.$t('selfservicemytask.confirmapprove')
            this.subDialogText = this.$t('selfservicemytask.approvetask')
            this.subDetailTask = param
        },

        // Confirmation Popup
        saveAction($event, param) {
            console.log('Save')
            console.log($event);
            console.log(param);

            // Perform Action to final approve
            if ($event == 1) {
                this.taskFinalApproval(param)
            }

            // Perform Action to reject
            if ($event == 2) {
                this.taskApproval('REJECT', param)
            }

            // Perform Action to approve
            if ($event == 3) {
                this.taskCheckApproval(param)
            }

            this.resetSubDialog();
        },

        cancelAction() {
            console.log('Cancel')
            this.resetSubDialog();
        },

        // Reset All
        resetSubDialog() {
            this.subDialogId = null
            this.subDialog = false
            this.subDialogTitle = ''
            this.subDialogText = ''
            this.subDialogTask = null
            this.subDialogStaffList = false
            this.subDialogStaffListParam = null
        },

        taskApproval(text, value) {

            value.apraction = text

            const vm = this;
            vm.loading = true;

            this.$store.dispatch('put', { url: "/approvalworkflow/" + this.$store.getters.userId + '/' + this.$store.getters.staffId, payload: value })
                .then((res) => {
                    const result = res.data;
                    if (result.status === 200) {
                        if (result.needsContinueOrSelect) {
                            this.pendingApprovalValue = value
                            this.pendingSkipLevelNo = result.skipLevelNo
                            this.pendingContinueAllowed = result.continueAllowed !== false
                            this.continueOrSelectMessage = result.message
                            this.continueOrSelectDialog = true
                        } else if (result.waitingForCoApprover || result.workflowSetupIncomplete) {
                            this.errorMessage2 = result.message
                            this.errorDialog2 = true
                        } else {
                            vm.$router.push({ name: 'selfservicemytask' });
                        }
                    } else {
                        console.error(result.error);
                        console.log("putting task approval error", result.error);

                        this.errorMessage2 = "Failed to save the record"
                        this.errorDialog2 = true
                    }

                    vm.loading = false;

                }).catch((err) => {
                    vm.loading = false;
                    console.error(err)
                    console.log("try catch error, putting task approval error", err);

                    this.errorMessage2 = "Failed to save the record"
                    this.errorDialog2 = true
                });

        },

        goBackHandler() {
            this.$router.push({ name: 'selfservicemytask' });
        },

        continueOrSelectYes() {
            this.continueOrSelectDialog = false
            const value = this.pendingApprovalValue
            this.pendingApprovalValue = null
            value.continueSkip = true
            value.skipLevelNo = this.pendingSkipLevelNo
            this.taskApproval('APPROVE', value)
        },

        continueOrSelectSelect() {
            this.continueOrSelectDialog = false
            this.subDialogStaffListParam = this.pendingApprovalValue
            this.subDialogStaffList = true
        },

        taskFinalApproval(value) {

            const vm = this;
            vm.loading = true;

            this.$store.dispatch('put', { url: "/finalapprovalworkflow/approve", payload: value })
                .then((res) => {
                    const result = res.data;
                    if (result.status === 200) {
                        vm.$router.push({ name: 'selfservicemytask' });
                    } else {
                        console.error(result.error);
                    }

                    vm.loading = false;

                }).catch((err) => {
                    vm.loading = false;
                    console.error(err)
                });

        },

        taskCheckApproval(value) {
            // The server resolves who (if anyone) handles the next level - role-based,
            // reporting-position chain, or HR-final-handoff - and tells us via the approve
            // response if it needs a manual pick (needsContinueOrSelect), so just approve
            // directly and react to whatever it says.
            this.taskApproval('APPROVE', value)
        },

        // Staff List Action
        backAction() {
            this.subDialogStaffList = false
            this.subDialogStaffListParam = null
        },

        // Function receive from chosen staff to fill an empty approval level
        choosenPerson(id, data) {
            this.subDialogStaffList = false
            data.selectedApproverStaffId = id
            data.skipLevelNo = this.pendingSkipLevelNo
            this.taskApproval('APPROVE', data)
        },

        onClickDate(d) {
            this.loaddate = moment(d).format("DD-MM-YYYY")
            this.loadRecordList(moment(d).format("YYYY-MM-DD"));
        },

        loadRecordList(date) {
            let dataholder = [];

            let getdatadefault = this.leaverecordList.filter(x => {
                return x.cdate == date
            })

            let getdatafilter = this.loadDateRecord.filter(x => {
                return x.cdate == date
            })

            this.loadDateRecord.length > 0 ? dataholder = getdatafilter : dataholder = getdatadefault

            for (let i = 0; i < dataholder.length; i++) {
                if (dataholder[i].lvstatusid == 1) dataholder[i].lvlstatusholder = '(P)'
                if (dataholder[i].lvstatusid == 2) dataholder[i].lvlstatusholder = '(A)'
                if (dataholder[i].lvstatusid == 3) dataholder[i].lvlstatusholder = '(R)'
                if (dataholder[i].lvstatusid == 4) dataholder[i].lvlstatusholder = '(C)'
            }

            let data = this.removeDuplicateKeys(dataholder);
            this.loadStaffListLeave = data;
        },

        removeDuplicateKeys(arr) {
            const ids = arr.map(o => o.listid)
            const filtered = arr.filter(({ listid }, index) => !ids.includes(listid, index + 1))
            return filtered;
        },

        // loadRecordCountList() {
        //     const vm = this;
        //     //let itemholder = []
        //     let id = 0

        //     // console.log(this.$store.getters.userId)
        //     // console.log(vm.startMonthDate)
        //     // console.log(vm.endMonthDate)
        //     // console.log(this.$store.getters.staffId)

        //     //this.$store.dispatch('get', {url: `/leaverecordwho/all/${this.$store.getters.userId}/${vm.startMonthDate}/${vm.endMonthDate}`})
        //     //this.$store.dispatch('get', { url: `/leaverecordwhoselfservice/all/${vm.startMonthDate}/${vm.endMonthDate}`})
        //     this.$store.dispatch('get', { url: `/leaverecordwho/all`})            
        //     .then(async (res) => {

        //     let dataitems = res.data.rec;
        //     vm.backupDataItems = dataitems;
        //     console.log('Init Data', dataitems)

        //     for (let i = 0; i < dataitems.length; i++) {
        //         let leaved = await this.checkDate(dataitems[i])
        //         dataitems[i]. tleave= leaved.hday
        //         id++
        //     }

        //     if (id == dataitems.length) {

        //         // dataitems.map(val => {
        //         // let it2 = this.totalStaffMov.filter(val2 => {
        //         //     return val2.staffid == val.staffid && val2.recdate <= val.startdate && val2.recenddate >= val.enddate 
        //         // })[0]
        //         // val.emptypeid = it2.emptypeid
        //         // val.brchid = it2.brchid
        //         // val.compnyid = it2.compnyid
        //         // val.deptid = it2.deptid
        //         // val.gradeid = it2.grdid
        //         // return val
        //         // })

        //         let dateh = []
        //         for (let i = 0; i < dataitems.length; i++) {
        //             let getd = [...new Set(dataitems[i].tleave.map(item => item))]
        //             dateh = dateh.concat(getd)
        //         }
        //         let fdate = [...new Set(dateh.map(x => x))]
        //         let g = 0
        //         for (let i = 0; i < fdate.length; i++) {
        //             let dt = dataitems.filter(x => {
        //             let hol = x.tleave.indexOf(fdate[i])
        //             if (hol == -1) {
        //                 return false
        //             }
        //             return x
        //         })

        //         let newdt = [...dt]

        //         for (let j = 0; j < newdt.length; j++) {
        //             let itm = {...newdt[j], cdate: fdate[i], listid: g++}
        //             vm.leaverecordList.push(itm)
        //         }

        //         vm.calendaritems.push({
        //             id: i+1,
        //             startDate: fdate[i],
        //             title: newdt.length
        //         })

        //         }

        //         console.log('Test Data', vm.leaverecordList)
        //         this.loadRecordToday(vm.leaverecordList);
        //         this.loadRecordTomorrow(vm.leaverecordList);
        //     }
        //     })
        //     .catch((err) => {
        //         //req failure --> emit error
        //         vm.isErrTrigger = true;
        //         vm.errMsg = err;
        //     });
        // }, // V0, not await 

        // async loadRecordCountList() {
        //     const vm = this;
        //     this.leaverecordList = [];
        //     this.calendaritems = [];

        //     let id = 0;

        //     try {
        //         const res = await this.$store.dispatch('get', {
        //             url: `/leaverecordwho/all`
        //         });

        //         let dataitems = res.data.rec || [];
        //         vm.backupDataItems = dataitems;

        //         console.log('Calander Init Data', dataitems);

        //         // Process leave days
        //         for (let i = 0; i < dataitems.length; i++) {
        //             let leaved = await this.checkDate(dataitems[i]);
        //             dataitems[i].tleave = leaved.hday;
        //             id++;
        //         }

        //         if (id === dataitems.length) {

        //             let dateh = [];

        //             // Collect all unique leave dates
        //             for (let i = 0; i < dataitems.length; i++) {
        //                 let getd = [...new Set(dataitems[i].tleave.map(item => item))];
        //                 dateh = dateh.concat(getd);
        //             }

        //             let fdate = [...new Set(dateh)];
        //             let g = 0;

        //             for (let i = 0; i < fdate.length; i++) {

        //                 let dt = dataitems.filter(x => {
        //                     return x.tleave.indexOf(fdate[i]) !== -1;
        //                 });

        //                 let newdt = [...dt];

        //                 for (let j = 0; j < newdt.length; j++) {
        //                     let itm = {
        //                         ...newdt[j],
        //                         cdate: fdate[i],
        //                         listid: g++
        //                     };

        //                     vm.leaverecordList.push(itm);
        //                 }

        //                 vm.calendaritems.push({
        //                     id: i + 1,
        //                     startDate: fdate[i],
        //                     title: newdt.length
        //                 });
        //             }

        //             console.log('Test Data', vm.leaverecordList);

        //             await this.loadRecordToday(vm.leaverecordList);
        //             await this.loadRecordTomorrow(vm.leaverecordList);
        //         }

        //     } catch (err) {
        //         vm.isErrTrigger = true;
        //         vm.errMsg = err;
        //     }
        // }, // V1, using old api call

        // async loadRecordCountList() {

        //     // clear state up front
        //     this.leaverecordList = [];
        //     this.calendaritems = [];

        //     try {
        //         this.loading = true;
        //         // const res = await this.$store.dispatch('get', {
        //         //     url: `/leaverecordwho/all/${this.$store.getters.userId}/${this.startMonthDate}/${this.endMonthDate}`
        //         // });
        //         const res = await this.$store.dispatch('get', {
        //             url: `/leaverecordwhoisonleave/${this.startMonthDate}/${this.endMonthDate}`
        //         });
        //         const dataitems = res.data.rec || [];
        //         this.backupDataItems = dataitems;

        //         // console.log('Calendar Init Data 1 ------ Tleave Data Check', dataitems);
        //         // Step 1 — attach tleave data
        //         // Here is the issue TODO:
        //         for (let i = 0; i < dataitems.length; i++) {

        //             const leaved = await this.checkDate(dataitems[i]);

        //             dataitems[i].tleave =
        //                 (leaved && leaved.hday)
        //                     ? leaved.hday
        //                     : [];
        //         }

        //         // Step 2 — collect unique dates
        //         let dateh = [];

        //         for (let i = 0; i < dataitems.length; i++) {
        //             const t = dataitems[i].tleave || [];
        //             dateh = dateh.concat(Array.from(new Set(t)));
        //         }

        //         const fdate = Array.from(
        //             new Set(dateh.map(d => moment(d).format("YYYY-MM-DD")))
        //         );

        //         // console.log("2----- Unique Dates", fdate);
        //         // Step 3 — build UI lists
        //         const builtLeaverecordList = [];
        //         const builtCalendarItems = [];

        //         let listIdCounter = 0;

        //         for (let i = 0; i < fdate.length; i++) {

        //             const day = fdate[i]; // ✅ IMPORTANT — you forgot this earlier

        //             const dt = dataitems.filter(x =>
        //                 Array.isArray(x.tleave) &&
        //                 x.tleave.includes(day)
        //             );

        //             const newdt = dt.map(item => ({
        //                 ...item,
        //                 cdate: day,
        //                 listid: listIdCounter++
        //             }));

        //             builtLeaverecordList.push(...newdt);

        //             builtCalendarItems.push({
        //                 id: i + 1,
        //                 startDate: day,
        //                 title: String(newdt.length)
        //             });
        //         }

        //         // Step 4 — assign reactive arrays
        //         this.leaverecordList = builtLeaverecordList;
        //         this.calendaritems = [...builtCalendarItems];

        //         this.calendarKey++;

        //         // Step 5 — update stats
        //         await this.loadRecordToday(this.leaverecordList);
        //         await this.loadRecordTomorrow(this.leaverecordList);

        //     } catch (err) {
        //         this.isErrTrigger = true;
        //         this.errMsg = err;
        //         console.error('loadRecordCount List error', err);
        //     }
        //     finally{
        //         this.loading = false;
        //     }
        // }, // v2, having checkdate, now dispose

        async loadRecordCountList() {

            // clear state up front
            this.leaverecordList = [];
            this.calendaritems = [];

            try {
                this.loading = true;
                // const res = await this.$store.dispatch('get', {
                //     url: `/leaverecordwho/all/${this.$store.getters.userId}/${this.startMonthDate}/${this.endMonthDate}`
                // });
                const res = await this.$store.dispatch('get', {
                    url: `/leaverecordwhoisonleave/${this.startMonthDate}/${this.endMonthDate}`
                });
                const dataitems = res.data.rec || [];
                this.backupDataItems = dataitems;

                // console.log('Calendar Init Data 1 ------ Tleave Data Check', dataitems);
                // Step 1 — attach tleave data directly on the client
                for (let i = 0; i < dataitems.length; i++) {
                    const start = moment(dataitems[i].startdate);
                    const end = moment(dataitems[i].enddate);
                    const localLeaveDays = [];

                    // Generate an array of all dates between start and end
                    let current = start.clone();
                    while (current.isSameOrBefore(end)) {
                        localLeaveDays.push(current.format('YYYY-MM-DD'));
                        current.add(1, 'days');
                    }

                    // Assign the generated dates straight to tleave
                    dataitems[i].tleave = localLeaveDays;
                }

                // Step 2 — collect unique dates
                let dateh = [];

                for (let i = 0; i < dataitems.length; i++) {
                    const t = dataitems[i].tleave || [];
                    dateh = dateh.concat(Array.from(new Set(t)));
                }

                const fdate = Array.from(
                    new Set(dateh.map(d => moment(d).format("YYYY-MM-DD")))
                );

                // console.log("2----- Unique Dates", fdate);
                // Step 3 — build UI lists
                const builtLeaverecordList = [];
                const builtCalendarItems = [];

                let listIdCounter = 0;

                for (let i = 0; i < fdate.length; i++) {

                    const day = fdate[i]; // ✅ IMPORTANT — you forgot this earlier

                    const dt = dataitems.filter(x =>
                        Array.isArray(x.tleave) &&
                        x.tleave.includes(day)
                    );

                    const newdt = dt.map(item => ({
                        ...item,
                        cdate: day,
                        listid: listIdCounter++
                    }));

                    builtLeaverecordList.push(...newdt);

                    builtCalendarItems.push({
                        id: i + 1,
                        startDate: day,
                        title: String(newdt.length)
                    });
                }

                // Step 4 — assign reactive arrays
                this.leaverecordList = builtLeaverecordList;
                this.calendaritems = [...builtCalendarItems];

                this.calendarKey++;

                // Step 5 — update stats
                await this.loadRecordToday(this.leaverecordList);
                await this.loadRecordTomorrow(this.leaverecordList);

            } catch (err) {
                this.isErrTrigger = true;
                this.errMsg = err;
                console.error('loadRecordCount List error', err);
            }
            finally {
                this.loading = false;
            }
        }, // V3, directly apply the leave record to the calendar

        // async checkDate(item) {
        //     const vm = this

        //     const dataholder = {
        //         staffid: item.staffid,
        //         leavetypeid: item.leavetypeid,
        //         startdate: moment(item.startdate).format('YYYY-MM-DD'),
        //         enddate: moment(item.enddate).format('YYYY-MM-DD'),
        //         userid: vm.$store.getters.userId
        //     }

        //     let leavedays = this.$store.dispatch('post', {url: "/leave/leaveday", payload: dataholder})
        //         .then(res => {
        //             let item = res.data.rec
        //             item.hday = item.normalday.filter(val => {
        //                 return val >= dataholder.startdate && val <= dataholder.enddate
        //             })
        //             return item
        //         })
        //         .catch(err => {
        //             //error - unsucessfull request
        //             this.isErr = true;
        //             this.errMsg = err;
        //         });

        //     return leavedays
        // }, // V0, not await

        // async checkDate(item) {
        //     const vm = this;

        //     const dataholder = {
        //         staffid: item.staffid,
        //         leavetypeid: item.leavetypeid,
        //         startdate: moment(item.startdate).format('YYYY-MM-DD'),
        //         enddate: moment(item.enddate).format('YYYY-MM-DD'),
        //         userid: vm.$store.getters.userId
        //     };

        //     try {
        //         const res = await this.$store.dispatch('post', {
        //             url: "/leave/leaveday",
        //             payload: dataholder
        //         });

        //         let result = res.data.rec;
        //         // console.log("what is the result from post request?", res.data.rec);
        //         result.hday = result.normalday.filter(val => {
        //             return val >= dataholder.startdate &&
        //                 val <= dataholder.enddate;
        //         });

        //         return result;

        //     } catch (err) {
        //         // unsuccessful request
        //         this.isErr = true;
        //         this.errMsg = err;
        //         throw err; // important: propagate error to caller
        //     }
        // }, v1, no other function calling this

        // loadRecordToday(data) {

        //     this.todaydate = moment().format('DD-MM-YYYY');
        //     let todayholder = moment().format('YYYY-MM-DD');

        //     let dataholder = data.filter(x => {
        //     return x.cdate == todayholder
        //     })

        //     for (let i = 0; i < dataholder.length; i++) {
        //     if (dataholder[i].lvstatusid == 1) dataholder[i].lvlstatusholder = '(P)'
        //     if (dataholder[i].lvstatusid == 2) dataholder[i].lvlstatusholder = '(A)'
        //     if (dataholder[i].lvstatusid == 3) dataholder[i].lvlstatusholder = '(R)'
        //     if (dataholder[i].lvstatusid == 4) dataholder[i].lvlstatusholder = '(C)'
        //     }

        //     let dataNotDuplicate = this.removeDuplicateKeys(dataholder);
        //     this.todayStaffListLeave = dataNotDuplicate
        //     this.todayStaffListHolder = dataNotDuplicate
        //     this.totalStaffPresentToday = this.countStaffPresent(dataNotDuplicate.length)

        // },

        async loadRecordToday(data) {
            this.todaydate = moment().format('DD-MM-YYYY');
            const todayholder = moment().format('YYYY-MM-DD');

            let dataholder = data.filter(x => x.cdate === todayholder);

            for (let i = 0; i < dataholder.length; i++) {
                if (dataholder[i].lvstatusid == 1) dataholder[i].lvlstatusholder = '(P)';
                if (dataholder[i].lvstatusid == 2) dataholder[i].lvlstatusholder = '(A)';
                if (dataholder[i].lvstatusid == 3) dataholder[i].lvlstatusholder = '(R)';
                if (dataholder[i].lvstatusid == 4) dataholder[i].lvlstatusholder = '(C)';
            }

            const dataNotDuplicate = await this.removeDuplicateKeys(dataholder);

            this.todayStaffListLeave = dataNotDuplicate;
            this.todayStaffListHolder = dataNotDuplicate;
            this.totalStaffPresentToday = await this.countStaffPresent(dataNotDuplicate.length);
        },

        // loadRecordTomorrow(data) {

        //     this.tomorrowdate = moment().add(1,'days').format('DD-MM-YYYY');
        //     let tomorrowholder = moment().add(1,'days').format('YYYY-MM-DD');

        //     let dataholder = data.filter(x => {
        //     return x.cdate == tomorrowholder
        //     })

        //     for (let i = 0; i < dataholder.length; i++) {
        //     if (dataholder[i].lvstatusid == 1) dataholder[i].lvlstatusholder = '(P)'
        //     if (dataholder[i].lvstatusid == 2) dataholder[i].lvlstatusholder = '(A)'
        //     if (dataholder[i].lvstatusid == 3) dataholder[i].lvlstatusholder = '(R)'
        //     if (dataholder[i].lvstatusid == 4) dataholder[i].lvlstatusholder = '(C)'
        //     }

        //     let dataNotDuplicate = this.removeDuplicateKeys(dataholder);
        //     this.tomorrowStaffListLeave = dataNotDuplicate
        //     this.totalStaffPresentTomorrow = this.countStaffPresent(dataNotDuplicate.length)

        // },

        async loadRecordTomorrow(data) {
            this.tomorrowdate = moment().add(1, 'days').format('DD-MM-YYYY');
            const tomorrowholder = moment().add(1, 'days').format('YYYY-MM-DD');

            let dataholder = data.filter(x => x.cdate === tomorrowholder);

            for (let i = 0; i < dataholder.length; i++) {
                if (dataholder[i].lvstatusid == 1) dataholder[i].lvlstatusholder = '(P)';
                if (dataholder[i].lvstatusid == 2) dataholder[i].lvlstatusholder = '(A)';
                if (dataholder[i].lvstatusid == 3) dataholder[i].lvlstatusholder = '(R)';
                if (dataholder[i].lvstatusid == 4) dataholder[i].lvlstatusholder = '(C)';
            }

            const dataNotDuplicate = await this.removeDuplicateKeys(dataholder);

            this.tomorrowStaffListLeave = dataNotDuplicate;
            this.totalStaffPresentTomorrow = await this.countStaffPresent(dataNotDuplicate.length);
        },

        countStaffPresent(val) {
            const staffholder = this.totalStaff.length
            if (staffholder === 0) return 0;
            let item = ((staffholder - val) / staffholder) * 100
            return item.toFixed(2)
        },

        // loadTotalStaff() {
        //     const vm = this;

        //     this.$store.dispatch('get', {url: "/staff/alls"})
        //     .then((res) => {
        //     this.totalStaff = res.data.rec
        //     this.loadStaffMov();
        //     })
        //     .catch((err) => {
        //     //req failure --> emit error
        //     vm.isErrTrigger = true;
        //     vm.errMsg = err;
        //     });
        // },

        async loadTotalStaff() {
            const vm = this;

            try {
                const res = await this.$store.dispatch('get', {
                    url: "/staff/alls"
                });

                this.totalStaff = res.data.rec;

                await this.loadStaffMov();   // ensure sequential execution

            } catch (err) {
                vm.isErrTrigger = true;
                vm.errMsg = err;
            }
        },

        // loadStaffMov() {
        //     const vm = this;

        //     this.$store.dispatch('get', {url: `/staffmov/all/${this.$store.getters.userId}`})
        //     // this.$store.dispatch('get', {url: `/staffmov/all`})
        //     .then((res) => {
        //     this.totalStaffMov = res.data.rec
        //     console.log("Test To STaff Move", this.totalStaffMov);
        //     this.loadRecordCountList();
        //     })
        //     .catch((err) => {
        //     //req failure --> emit error
        //     vm.isErrTrigger = true;
        //     vm.errMsg = err;
        //     });
        // },

        async loadStaffMov() {
            const vm = this;

            try {
                // Wilson: Dont know why it called this api, but the result is not using anywhere
                // const res = await this.$store.dispatch('get', {
                //     url: `/staffmov/all/${this.$store.getters.userId}`
                // });

                // this.totalStaffMov = res.data.rec;
                // console.log("Test To Staff Move", this.totalStaffMov);

                await this.loadRecordCountList();  // maintain original chain behavior

            } catch (err) {
                vm.isErrTrigger = true;
                vm.errMsg = err;
            }
        },

        setShowDate(d) {
            this.showDate = d;
            this.startMonthDate = moment(this.showDate).startOf('month').format("YYYY-MM-DD");
            this.endMonthDate = moment(this.showDate).endOf('month').format("YYYY-MM-DD");
        },

        async checkFilter() {

            const vm = this;
            // console.log("what is the staff we have now",this.staff,"and the backupdataitem",this.backupDataItems)
            if (this.samecompany && !this.samedept) {

                // console.log('Filterting company')

                const filteredArray = vm.backupDataItems.filter(obj1 => {
                    return vm.staff.some(obj2 => obj1.compnyid === obj2.compnyid);
                });

                await vm.loadCalendar(filteredArray)

            }

            if (this.samedept && !this.samecompany) {

                // console.log('Filtering department')

                const filteredArray = vm.backupDataItems.filter(obj1 => {
                    return vm.staff.some(obj2 => obj1.deptid === obj2.deptid);
                });

                await vm.loadCalendar(filteredArray);

            }

            if (this.samedept && this.samecompany) {

                // console.log(this.samedept)
                // console.log(this.samecompany)
                // console.log("filterting both condition");

                const filteredArray = vm.backupDataItems.filter(obj1 => {
                    return vm.staff.some(obj2 => obj1.compnyid === obj2.compnyid && obj1.deptid === obj2.deptid);
                });

                await vm.loadCalendar(filteredArray)

            }

            if (!this.samedept && !this.samecompany) {

                // console.log('No filter')

                await vm.loadCalendar(vm.backupDataItems)
            }

        },

        loadAttachment(filetype, attachment) {

            if (filetype == 'PDF') {
                const blob = this.b64toBlob(attachment, 'application/pdf', 512)
                const url = URL.createObjectURL(blob);
                window.open(url);
            }

            if (filetype == 'Image') {
                var windowHandle = window.open("");
                windowHandle.document.write("<img src='" + attachment + "' />");
            }
        },

        b64toBlob(b64Data, contentType, sliceSize = 512) {
            contentType = contentType || ''
            sliceSize = sliceSize || 512
            b64Data = b64Data.replace(/^[^,]+,/, '')
            b64Data = b64Data.replace(/\s/g, '')

            var byteCharacters = atob(b64Data)
            var byteArrays = []

            for (var offset = 0; offset < byteCharacters.length; offset += sliceSize) {
                var slice = byteCharacters.slice(offset, offset + sliceSize)

                var byteNumbers = new Array(slice.length)
                for (var i = 0; i < slice.length; i++) {
                    byteNumbers[i] = slice.charCodeAt(i)
                }

                var byteArray = new Uint8Array(byteNumbers)

                byteArrays.push(byteArray)
            }

            var blob = new Blob(byteArrays, {
                type: contentType
            })
            // return byteCharacters;
            return blob
        },

        // loadStaffDetails() {

        //     const vm = this;

        //     this.$store.dispatch('get', { url: `/selservicestaff/${this.$store.getters.staffId}`})
        //     .then((res) => {
        //         vm.staff = res.data.rec;
        //         console.log("loadstaffdetail called: ",vm.staff)
        //     })
        //     .catch((err) => {
        //         console.log(err)
        //     });

        // },

        async loadStaffDetails() {
            const vm = this;

            try {
                const res = await this.$store.dispatch('get', {
                    url: `/selservicestaff/${this.$store.getters.staffId}`
                });

                vm.staff = res.data.rec;
                // console.log("loadstaffdetail called: ", vm.staff);

            } catch (err) {
                console.log(err);
            }
        },

        // async loadCalendar(filterData) {

        //     console.log('Filter Data', filterData);

        //     const vm = this;

        //     vm.calendaritems = []
        //     vm.leaverecordList = []

        //     let id = 0

        //     let dataitems = filterData;
        //     console.log('Init Data', dataitems)

        //     if(dataitems.length > 0){

        //         for (let i = 0; i < dataitems.length; i++) {
        //             let leaved = await this.checkDate(dataitems[i])
        //             dataitems[i]. tleave= leaved.hday
        //             id++
        //         }

        //         if (id == dataitems.length) {

        //             // dataitems.map(val => {
        //             // let it2 = this.totalStaffMov.filter(val2 => {
        //             //     return val2.staffid == val.staffid && val2.recdate <= val.startdate && val2.recenddate >= val.enddate 
        //             // })[0]
        //             // val.emptypeid = it2.emptypeid
        //             // val.brchid = it2.brchid
        //             // val.compnyid = it2.compnyid
        //             // val.deptid = it2.deptid
        //             // val.gradeid = it2.grdid
        //             // return val
        //             // })

        //             let dateh = []

        //             for (let i = 0; i < dataitems.length; i++) {
        //                 let getd = [...new Set(dataitems[i].tleave.map(item => item))]
        //                 dateh = dateh.concat(getd)
        //             }

        //             let fdate = [...new Set(dateh.map(x => x))]
        //             let g = 0

        //             for (let i = 0; i < fdate.length; i++) {

        //                 let dt = dataitems.filter(x => {
        //                     let hol = x.tleave.indexOf(fdate[i])
        //                     if (hol == -1) {
        //                         return false
        //                     }
        //                     return x
        //                 })

        //             let newdt = [...dt]

        //             for (let j = 0; j < newdt.length; j++) {
        //                 let itm = {...newdt[j], cdate: fdate[i], listid: g++}
        //                 vm.leaverecordList.push(itm)
        //             }

        //             vm.calendaritems.push({
        //                 id: i+1,
        //                 startDate: fdate[i],
        //                 title: newdt.length
        //             })
        //             }

        //             console.log('Test Data', vm.leaverecordList)
        //             this.loadRecordToday(vm.leaverecordList);
        //             this.loadRecordTomorrow(vm.leaverecordList);

        //         }

        //     } else {

        //         console.log('Hello')

        //         vm.calendaritems = [];
        //         vm.leaverecordList = [];

        //     }

        // }, // v0, using check date

        async loadCalendar(filterData) {
            // console.log('Filter Data', filterData);

            this.calendaritems = [];
            this.leaverecordList = [];

            if (!filterData || filterData.length === 0) {
                // console.log('No Data exsit, dont need prompt error , just exsit early');
                // vm.isErrTrigger = true;
                // vm.errMsg = "No calandar Data to show"; // should not prompt this, just empty for the calandar
                return;
            }

            // Create a shallow copy to avoid directly mutating the original filterData
            let dataitems = [...filterData];
            // console.log('Init Data', dataitems);

            // Step 1: Generate dates client-side (No more checkDate server calls!)
            for (let i = 0; i < dataitems.length; i++) {
                const start = moment(dataitems[i].startdate);
                const end = moment(dataitems[i].enddate);
                const localLeaveDays = [];

                let current = start.clone();
                while (current.isSameOrBefore(end)) {
                    localLeaveDays.push(current.format('YYYY-MM-DD'));
                    current.add(1, 'days');
                }

                dataitems[i].tleave = localLeaveDays;
            }

            // Step 2: Collect all unique dates across all staff
            let allDates = [];
            for (let i = 0; i < dataitems.length; i++) {
                allDates = allDates.concat(dataitems[i].tleave);
            }
            // Use Set to easily remove duplicates
            const uniqueDates = [...new Set(allDates)];

            // Step 3: Build the lists for the UI
            let listIdCounter = 0;

            for (let i = 0; i < uniqueDates.length; i++) {
                const currentDate = uniqueDates[i];

                // Filter items that have this date in their tleave array
                const itemsOnThisDate = dataitems.filter(x => x.tleave.includes(currentDate));

                // Map new objects and push them to leaverecordList
                for (let j = 0; j < itemsOnThisDate.length; j++) {
                    this.leaverecordList.push({
                        ...itemsOnThisDate[j],
                        cdate: currentDate,
                        listid: listIdCounter++
                    });
                }

                // Push to calendar UI
                this.calendaritems.push({
                    id: i + 1,
                    startDate: currentDate,
                    title: String(itemsOnThisDate.length)
                });
            }

            // console.log('Test Data', this.leaverecordList);

            // Step 4: Update stats
            await this.loadRecordToday(this.leaverecordList);
            await this.loadRecordTomorrow(this.leaverecordList);
        }, // V1, now use same push data method as the loadRecordCountList() v3

    },

    async mounted() {
        this.loading = true;
        await this.loadStaffDetails();
        await this.loadTotalStaff();
        this.loading = false;
        // console.log("leaveDetail",this.leaveDetail);
        // console.log("staffApproveList",this.staffApproveList);
    },
}
</script>

<style>
.la-card {
    background: #ffffff;
    border-radius: 12px;
    border: 1px solid #E8EDF5;
    box-shadow: 0 1px 6px rgba(0,0,0,0.05);
    margin-bottom: 8px;
    overflow: hidden;
}
.la-card-header {
    display: flex;
    align-items: center;
    gap: 8px;
    background: #F1F5F9;
    padding: 10px 16px;
    font-size: 0.82rem;
    font-weight: 600;
    color: #1a3a5c;
    letter-spacing: normal;
    border-bottom: 1px solid #E8EDF5;
}
.la-card-header-green { background: linear-gradient(135deg, #059669, #15803D); color: #fff; }
.la-card-header-blue  { background: linear-gradient(135deg, #4F6BFF, #7C3AFF); color: #fff; }
.la-card-header-green svg, .la-card-header-blue svg { stroke: #fff; }
.la-card-body {
    padding: 12px 16px;
}
.la-row {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin-bottom: 8px;
    font-size: 0.82rem;
    letter-spacing: normal;
}
.la-label {
    font-size: 0.75rem;
    font-weight: 600;
    color: #546e7a;
    min-width: 110px;
    flex-shrink: 0;
    letter-spacing: normal;
    padding-top: 1px;
}
.la-value {
    font-size: 0.82rem;
    color: #1a3a5c;
    letter-spacing: normal;
}
.la-badge {
    display: inline-block;
    background: #EEF2FF;
    color: #4F6BFF;
    font-size: 0.72rem;
    font-weight: 600;
    border-radius: 10px;
    padding: 1px 8px;
    margin-left: 6px;
}
.la-btn-row {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    margin-top: 4px;
}
.la-action-bar {
    display: flex;
    align-items: center;
    gap: 10px;
    background: #F8FAFF;
    border-top: 1px solid #E8EDF5;
    padding: 12px 16px;
    border-radius: 0 0 12px 12px;
    flex-wrap: wrap;
}
.fbtn {
    min-width: 100px !important;
    height: 36px !important;
    border-radius: 8px !important;
    font-size: 0.82rem !important;
    font-weight: 600 !important;
    text-transform: none !important;
    letter-spacing: 0.02em !important;
    box-shadow: none !important;
}
.fbtn-success { background: #16A34A !important; color: #fff !important; }
.fbtn-success:hover { background: #15803D !important; }
.fbtn-danger  { background: #E11D48 !important; color: #fff !important; }
.fbtn-danger:hover  { background: #BE123C !important; }
.fbtn-secondary { background: #4F6BFF !important; color: #fff !important; }
.fbtn-secondary:hover { background: #3B55E6 !important; }
.la-btn {
    text-transform: none !important;
    letter-spacing: normal !important;
    font-size: 0.82rem !important;
    font-weight: 600 !important;
    border-radius: 8px !important;
    box-shadow: none !important;
}
.la-btn-success { background: #16A34A !important; color: #fff !important; }
.la-btn-error   { background: #E11D48 !important; color: #fff !important; }
.la-btn-primary { background: #4F6BFF !important; color: #fff !important; }
.la-approval-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 6px;
    flex-wrap: wrap;
    font-size: 0.82rem;
}
.la-status-badge {
    display: inline-block;
    font-size: 0.7rem;
    font-weight: 700;
    border-radius: 10px;
    padding: 2px 8px;
}
.badge-approved { background: #DCFCE7; color: #059669; }
.badge-rejected { background: #FEE2E2; color: #DC2626; }
.la-link { color: #4F6BFF; cursor: pointer; }
.la-link:hover { text-decoration: underline; }
.la-cal-date { font-size: 0.82rem; font-weight: 600; color: #4F6BFF; margin-bottom: 2px; }
.la-cal-label { color: #4F6BFF; }
.la-pct { color: #F59E0B; font-weight: 600; margin-left: 6px; }
.la-cal-item { font-size: 0.78rem; color: #37474f; margin-bottom: 2px; }
.la-grey { color: #78909C; }
.cal-a { color: #059669; font-weight: 700; }
.cal-p { color: #DC2626; font-weight: 700; }

#calendar { color: #2c3e50; height: 60vh; width: 100%; }
.theme-default .cv-event { background: none; border: none; color: red; font-size: 14px; }

.la-card-scroll {
    display: flex;
    flex-direction: column;
    max-height: 720px;
    overflow: hidden;
}
.la-card-scroll .la-card-header {
    flex-shrink: 0;
}
.la-scroll-body {
    overflow-y: auto;
    flex: 1;
}
.la-scroll-body::-webkit-scrollbar { width: 5px; }
.la-scroll-body::-webkit-scrollbar-track { background: #F8FAFF; }
.la-scroll-body::-webkit-scrollbar-thumb { background: #C7D2FE; border-radius: 4px; }
.la-vcard {
    border-radius: 12px !important;
    border: 1px solid #E8EDF5 !important;
    display: flex !important;
    flex-direction: column !important;
    max-height: 720px !important;
    overflow: hidden !important;
}
.la-vcard-title {
    background: linear-gradient(135deg, #4F6BFF 0%, #7C3AFF 100%) !important;
    color: #ffffff !important;
    font-size: 0.82rem !important;
    font-weight: 600 !important;
    letter-spacing: normal !important;
    padding: 10px 16px !important;
    flex-shrink: 0 !important;
    position: sticky !important;
    top: 0 !important;
    z-index: 2 !important;
}
.la-vcard-title svg { stroke: #fff; margin-right: 6px; }
.la-vcard-body {
    overflow-y: auto !important;
    flex: 1 !important;
    padding: 12px 16px !important;
}
.la-vcard-body::-webkit-scrollbar { width: 5px; }
.la-vcard-body::-webkit-scrollbar-track { background: #F8FAFF; }
.la-vcard-body::-webkit-scrollbar-thumb { background: #C7D2FE; border-radius: 4px; }
.la-calendar-card { border-radius: 12px; overflow: hidden; border: 1px solid #E8EDF5; box-shadow: 0 2px 8px rgba(0,0,0,0.05); }
.theme-modern-leave.cv-wrapper { font-family: 'Inter', 'Segoe UI', sans-serif !important; border: none !important; }
.theme-modern-leave .cv-header { background: linear-gradient(135deg, #4F6BFF 0%, #7C3AFF 100%) !important; border: none !important; padding: 14px 16px !important; }
.theme-modern-leave .cv-header button,
.theme-modern-leave .cv-header .periodLabel { color: #ffffff !important; font-weight: 600 !important; border: none !important; background: rgba(255,255,255,0.15) !important; border-radius: 8px !important; padding: 4px 10px !important; }
.theme-modern-leave .cv-header button:hover { background: rgba(255,255,255,0.28) !important; }
.theme-modern-leave .cv-header .periodLabel { background: transparent !important; font-size: 1.05rem !important; }
.theme-modern-leave .cv-weekdays { background: #F8FAFF !important; border-bottom: 1px solid #E8EDF5 !important; }
.theme-modern-leave .cv-weekday { font-size: 0.72rem !important; font-weight: 600 !important; color: #64748B !important; text-transform: uppercase !important; letter-spacing: 0.06em !important; padding: 8px 0 !important; border: none !important; }
.theme-modern-leave .cv-weeks { border: none !important; }
.theme-modern-leave .cv-week { border-bottom: 1px solid #F1F5F9 !important; min-height: 80px !important; }
.theme-modern-leave .cv-day { border-right: 1px solid #F1F5F9 !important; border-bottom: none !important; background: #fff !important; transition: background 0.15s !important; }
.theme-modern-leave .cv-day:hover { background: #F5F7FF !important; cursor: pointer; }
.theme-modern-leave .cv-day.today { background: #EEF2FF !important; }
.theme-modern-leave .cv-day.today .cv-day-number { background: #4F6BFF !important; color: #fff !important; border-radius: 50% !important; width: 26px !important; height: 26px !important; display: flex !important; align-items: center !important; justify-content: center !important; font-weight: 700 !important; }
.theme-modern-leave .cv-day-number { position: absolute !important; top: 0 !important; right: 0 !important; z-index: 2 !important; font-size: 0.82rem !important; color: #475569 !important; font-weight: 500 !important; padding: 6px 8px !important; }
.theme-modern-leave .cv-day.outsideOfMonth .cv-day-number { color: #CBD5E1 !important; }
.theme-modern-leave .cv-event { background: linear-gradient(135deg, #4F6BFF, #7C3AFF) !important; color: #fff !important; border: none !important; border-radius: 6px !important; font-size: 0.72rem !important; font-weight: 600 !important; padding: 2px 6px !important; margin: 1px 3px !important; box-shadow: 0 2px 6px rgba(79,107,255,0.3) !important; text-overflow: ellipsis !important; white-space: nowrap !important; overflow: hidden !important; }
</style>